import React, { useCallback, useEffect } from 'react';
import { COLORS, FlexView, Row } from '@cuemath/comps';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useParams } from 'react-router-dom';
import { TableRow } from 'semantic-ui-react';
import { useStudentCoursesGetAll, useStudentGet } from 'actions/studentsv2';
import { get as getTeacherData } from 'actions/teachers';
import { openModal } from 'actions/modal';
import { Heading, Table, Filters, ColoredChip, TableCell, TableInfoCell } from 'comps';
import { useAppContext } from '../../../app-context';
import { trackEvent } from 'modules/analytics';
import { getAll as getAllCourses } from 'actions/course-list';

import CourseActions from './actions-dropdown';
import styled from 'styled-components';
import { store } from 'redux-rest-reducer';
import { EVENT_TYPE } from 'constants/logger';

const TeacherNameWrapper = styled(Row)`
  :hover {
    text-decoration: underline;
    cursor: pointer;
  }
`;

const GET_TEACHER_QUERY_PARAMS = {
  with_teacher_band: true,
};

function Courses(props) {
  const { setSidePanelView } = useAppContext();
  const { id } = useParams();
  const {
    data: courses,
    getAll: getCourses,
    isProcessing,
    isStale,
  } = useStudentCoursesGetAll(id);
  const { data: studentDetails } = useStudentGet(id) || {};
  const { grade } = studentDetails || {};
  const { actions, teachers, courseList } = props;
  const courseListData = store.getAll(courseList);

  const fetchStudentCourses = useCallback(() => {
    getCourses(id, {
      include_teacher_details: true,
    });
  }, [getCourses, id]);

  useEffect(() => {
    fetchStudentCourses();
    trackEvent(EVENT_TYPE.COURSES_SCREEN_VIEW, {
      student_id: id,
    });
  }, [fetchStudentCourses, id]);

  useEffect(() => {
    if (isStale) {
      fetchStudentCourses();
    }
  }, [fetchStudentCourses, isStale]);

  useEffect(() => {
    if (courses?.length) {
      let teacherData = new Set();

      courses?.forEach(({ primary_teacher_id: primaryTeacherId }, i) => {
        if (primaryTeacherId && !teacherData.has(primaryTeacherId)) {
          actions.getTeacherData(primaryTeacherId, GET_TEACHER_QUERY_PARAMS);
          teacherData.add(primaryTeacherId);
        }
      });
    }
    actions.getAllCourses();
  }, [actions, courses]);

  useEffect(() => {
    if (courses?.length) {
      courses.forEach(data => {
        const { primary_teacher_id: primaryTeacherId } = data;

        if (primaryTeacherId) {
          const teacherProfileGet = store.get(
            teachers,
            primaryTeacherId,
            GET_TEACHER_QUERY_PARAMS,
          );

          if (teacherProfileGet && teacherProfileGet.isLoaded) {
            data['teacher'] = teacherProfileGet.data;
            data['teacher_name'] = teacherProfileGet.data.name;
          }
        } else {
          data['teacher_name'] = 'NA';
        }
      });
    }
  }, [courses, teachers]);

  const onViewCourseLog = (studentCourseId, studentId) => {
    actions.openModal(
      'studentCourseLogModal',
      {
        size: 'custom',
        width: `${Math.ceil((window.innerWidth * 50) / 100)}px`,
        height: 'fit-content',
      },
      { studentCourseId, studentId },
    );
  };

  return (
    <>
      <Row justifyContent="space-between">
        <FlexView>
          <Heading>Courses Info</Heading>
          <Filters filters={[]} />
        </FlexView>
      </Row>
      <Table
        headerCell={[
          { title: 'Action' },
          { title: 'Course name' },
          { title: 'Plan' },
          { title: 'Teacher' },
          { title: 'Class balance' },
          { title: 'Status' },
        ]}
        infoWidth="32px"
        showLoader={isProcessing}
        dataLength={courses?.length}
      >
        {courses?.map(course => {
          const {
            id: courseId,
            course_name: courseName,
            state,
            enrollment_type: enrollmentType,
            teacher_details: teacherDetails,
            class_ratio: classRatio,
            classes_balance: classBalance,
            classes_purchased: classPurchased,
            primary_teacher_id: primaryTeacherId,
          } = course;
          const { name: teacherName } = teacherDetails || {};
          const isEnrolled = !!enrollmentType;
          const haveClassRatio = !!classRatio;
          const enrollmentTypeText =
            isEnrolled && haveClassRatio
              ? `${enrollmentType} 1:${classRatio}`
              : isEnrolled && !classRatio
              ? enrollmentType
              : null;

          return (
            <TableRow key={courseId}>
              <TableInfoCell
                onClick={() =>
                  setSidePanelView({
                    view: 'course-info',
                    props: { ...course, onViewCourseLog, student_id: id },
                    title: 'Course Info',
                  })
                }
                width={1}
              />
              <TableCell width={1}>
                <CourseActions
                  grade={grade}
                  course={course}
                  actions={actions}
                  studentId={id}
                  courseListData={courseListData}
                />
              </TableCell>
              <TableCell width={3}>{courseName || '-'}</TableCell>
              <TableCell width={3}>
                {enrollmentTypeText ? (
                  <ColoredChip
                    variant={enrollmentType.toLowerCase()}
                    color={COLORS.WHITE}
                  >
                    {enrollmentTypeText}
                  </ColoredChip>
                ) : (
                  '-'
                )}
              </TableCell>
              <TableCell width={3}>
                <TeacherNameWrapper
                  variant="cta1"
                  onClick={() =>
                    primaryTeacherId &&
                    window.open(`/users/teachers/${primaryTeacherId}`, '_blank')
                  }
                >
                  {teacherName || '-'}
                </TeacherNameWrapper>
              </TableCell>
              <TableCell width={2}>
                {classPurchased >= 0 && classBalance >= 0
                  ? `${classBalance} / ${classPurchased}`
                  : '-'}
              </TableCell>
              <TableCell width={2}>{state.toLowerCase() || '-'}</TableCell>
            </TableRow>
          );
        })}
      </Table>
    </>
  );
}

export default connect(
  ({ teachers, courseList }) => ({
    teachers,
    courseList,
  }),
  dispatch => ({
    actions: bindActionCreators(
      {
        openModal,
        getTeacherData,
        getAllCourses,
      },
      dispatch,
    ),
  }),
)(Courses);
